/**
 * Production-ready error handling and logging system
 * Replaces console.log/error calls with proper error tracking
 */

class ErrorHandler {
  /**
   * Log error for debugging purposes (development only)
   * @param {string} context - The context where the error occurred
   * @param {Error|string} error - The error object or message
   * @param {Object} metadata - Additional metadata
   */
  static logError(context, error, metadata = {}) {
    if (process.env.NODE_ENV === 'development') {
      console.error(`[${context}]`, error, metadata);
    }
    
    // In production, you would send to error tracking service
    // Example: Sentry, LogRocket, etc.
    if (process.env.NODE_ENV === 'production') {
      // TODO: Implement production error tracking
      // Sentry.captureException(error, { tags: { context }, extra: metadata });
    }
  }

  /**
   * Log warning for debugging purposes (development only)
   * @param {string} context - The context where the warning occurred
   * @param {string} message - The warning message
   * @param {Object} metadata - Additional metadata
   */
  static logWarning(context, message, metadata = {}) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[${context}]`, message, metadata);
    }
  }

  /**
   * Log info for debugging purposes (development only)
   * @param {string} context - The context where the info occurred
   * @param {string} message - The info message
   * @param {Object} metadata - Additional metadata
   */
  static logInfo(context, message, metadata = {}) {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[${context}]`, message, metadata);
    }
  }

  /**
   * Handle messaging service errors with appropriate user feedback
   * @param {Error} error - The error object
   * @param {string} operation - The operation that failed
   * @returns {Object} User-friendly error response
   */
  static handleMessagingError(error, operation) {
    this.logError('MessagingService', error, { operation });
    
    // Return user-friendly error messages
    switch (error?.code) {
      case 'PGRST116': // No rows found
        return {
          success: false,
          error: 'Conversation not found',
          userMessage: 'This conversation may have been removed.'
        };
      
      case '23503': // Foreign key violation
        return {
          success: false,
          error: 'Invalid reference',
          userMessage: 'Unable to complete this action. Please try again.'
        };
        
      case '42501': // Insufficient privileges
        return {
          success: false,
          error: 'Permission denied',
          userMessage: 'You do not have permission to perform this action.'
        };
        
      default:
        return {
          success: false,
          error: error?.message || 'Unknown error',
          userMessage: 'Something went wrong. Please try again.'
        };
    }
  }

  /**
   * Handle attachment upload errors
   * @param {Error} error - The error object
   * @returns {Object} User-friendly error response
   */
  static handleUploadError(error) {
    this.logError('FileUpload', error);
    
    if (error?.message?.includes('File too large')) {
      return {
        success: false,
        error: 'File too large',
        userMessage: 'File size must be less than 10MB.'
      };
    }
    
    if (error?.message?.includes('Invalid file type')) {
      return {
        success: false,
        error: 'Invalid file type',
        userMessage: 'Only images, documents, and archives are allowed.'
      };
    }
    
    return {
      success: false,
      error: error?.message || 'Upload failed',
      userMessage: 'Failed to upload file. Please try again.'
    };
  }

  /**
   * Handle network connectivity errors
   * @param {Error} error - The error object
   * @returns {Object} User-friendly error response
   */
  static handleNetworkError(error) {
    this.logError('Network', error);
    
    return {
      success: false,
      error: 'Network error',
      userMessage: 'Please check your internet connection and try again.'
    };
  }
}

export default ErrorHandler;
